"use client";

import Link from "next/link";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../../lib";

export interface GlowButtonProps
	extends Omit<ComponentProps<typeof Link>, "className" | "children"> {
	children: ReactNode;
	className?: string;
	/** Optional leading icon or glyph. */
	icon?: ReactNode;
	/** Tighter horizontal spacing (e.g. footer attribution links). */
	compact?: boolean;
}

/**
 * Commit / Tailwind Plus–style subtle link control with hover glow (for docs, GitHub, RSS, etc.).
 */
export function GlowButton({
	children,
	className,
	icon,
	compact = false,
	...linkProps
}: GlowButtonProps) {
	return (
		<Link
			{...linkProps}
			className={cn(
				"group relative isolate flex items-center rounded-lg px-2 py-0.5 text-[0.8125rem]/6 font-medium text-white/30 transition-colors hover:text-sky-300",
				compact ? "gap-x-2" : "gap-x-3",
				className,
			)}
		>
			<span className="absolute inset-0 -z-10 scale-75 rounded-lg bg-white/5 opacity-0 transition group-hover:scale-100 group-hover:opacity-100" />
			{icon ? (
				<span className="h-4 w-4 flex-none [&>svg]:size-4">{icon}</span>
			) : null}
			<span className="self-baseline text-white">{children}</span>
		</Link>
	);
}

export interface GlowButtonRowProps {
	children: ReactNode;
	className?: string;
}

export function GlowButtonRow({ children, className }: GlowButtonRowProps) {
	return (
		<div
			className={cn(
				"mt-8 flex flex-wrap justify-center gap-x-1 gap-y-3 sm:gap-x-2 lg:justify-start",
				className,
			)}
		>
			{children}
		</div>
	);
}
